import { useEffect, useState } from "react";
import api from "@/lib/api";
import { CalendarClock } from "lucide-react";

const DAY_LABELS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

function fmtHHMM(min) {
    if (!min) return "";
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${h}:${m.toString().padStart(2, "0")}`;
}

function fmtHHMMSigned(min) {
    if (!min) return "0:00";
    const abs = Math.abs(min);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return `${min < 0 ? "-" : ""}${h}:${m.toString().padStart(2, "0")}`;
}

const STATUS = {
    work: { label: "Trabaja", bg: "#D1FAE5", border: "#6EE7B7", color: "#064E3B" },
    vacation: { label: "Vacaciones", bg: "#FEF3C7", border: "#FCD34D", color: "#92400E" },
    sick: { label: "Baja", bg: "#FEE2E2", border: "#FCA5A5", color: "#7F1D1D" },
    permission: { label: "Permiso", bg: "#FED7AA", border: "#FB923C", color: "#7C2D12" },
    unavailable: { label: "Libre", bg: "#F4F4F5", border: "#D4D4D8", color: "#52525B" },
    manual: { label: "Manual", bg: "#DBEAFE", border: "#93C5FD", color: "#1E3A8A" },
};

export default function OperatorSchedule() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/operator/schedule").then((r) => setData(r.data)).finally(() => setLoading(false));
    }, []);

    if (loading || !data) return <div className="zentra-skeleton h-96" />;
    const todayIso = new Date().toISOString().slice(0, 10);

    return (
        <div className="space-y-6" data-testid="op-schedule">
            <div>
                <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                    Mi cuadrante semanal
                </p>
                <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1">Mi horario</h2>
                <p className="text-sm text-zentra-muted mt-1">
                    Semana: {data.week_start} → {data.week_end}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <KpiCell label="Horas contratadas / sem" value={fmtHHMM(data.contracted_minutes)} testId="op-contracted" />
                <KpiCell label="Horas asignadas / sem" value={fmtHHMM(data.assigned_minutes)} testId="op-assigned" />
                <KpiCell
                    label="Cómputo (real − contratado)"
                    value={fmtHHMMSigned(data.delta_minutes)}
                    tone={data.delta_minutes === 0 ? "ok" : Math.abs(data.delta_minutes) > 120 ? "bad" : "warn"}
                    testId="op-delta"
                />
            </div>

            <div className="space-y-2" data-testid="op-schedule-days">
                {data.days.map((d, i) => {
                    const isToday = d.date === todayIso;
                    const ov = d.override;
                    const status = ov?.status || (d.minutes > 0 ? "work" : "unavailable");
                    const meta = STATUS[status] || STATUS.work;
                    return (
                        <div
                            key={d.date}
                            className={`zentra-card-static p-4 flex items-center gap-4 ${isToday ? "ring-2 ring-zentra-accent" : ""}`}
                            style={{ borderLeft: `4px solid ${meta.border}` }}
                            data-testid={`op-day-${d.date}`}
                        >
                            <div className="w-20 shrink-0">
                                <p className="text-[10px] font-semibold tracking-widest uppercase text-zentra-muted">
                                    {DAY_LABELS[i]}
                                    {isToday && <span className="text-zentra-accent-strong ml-1">· HOY</span>}
                                </p>
                                <p className="font-display text-2xl font-bold text-zentra-ink leading-none mt-1">
                                    {new Date(d.date).getDate()}
                                </p>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p
                                    className="inline-block px-2 py-0.5 rounded-md text-xs font-semibold"
                                    style={{ background: meta.bg, color: meta.color }}
                                >
                                    {meta.label}
                                </p>
                                {d.tasks_count > 0 && (
                                    <p className="text-xs text-zentra-muted mt-1.5">
                                        {d.tasks_count} {d.tasks_count === 1 ? "tarea asignada" : "tareas asignadas"}
                                    </p>
                                )}
                                {ov?.note && <p className="text-xs text-zentra-muted mt-1 italic">{ov.note}</p>}
                            </div>
                            <div className="text-right">
                                <p className="text-[10px] uppercase tracking-widest text-zentra-muted">Horas</p>
                                <p className="font-display text-xl font-bold text-zentra-ink font-mono-zentra">
                                    {fmtHHMM(d.minutes) || "—"}
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function KpiCell({ label, value, tone, testId }) {
    const toneCls = tone === "bad"
        ? "bg-red-50 border-red-200 text-red-800"
        : tone === "warn"
            ? "bg-amber-50 border-amber-200 text-amber-800"
            : tone === "ok"
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : "bg-white border-zentra-border text-zentra-ink";
    return (
        <div className={`rounded-lg border p-4 ${toneCls}`} data-testid={testId}>
            <p className="text-[10px] uppercase tracking-widest text-zentra-muted">{label}</p>
            <p className="font-display font-bold text-2xl mt-1 font-mono-zentra">{value}</p>
        </div>
    );
}
